import { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Paper, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ptPT, enUS } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import pt from 'dayjs/locale/pt';
import { useAgendaTranslation as useTranslation } from '../translations';

dayjs.locale({ ...pt, name: 'pt-agenda', weekStart: 0 }, null, true);

const RangeDay = forwardRef(function RangeDay(
    { day, range, onSelect, onDaySelect, ...props },
    ref
) {
    const date = day.format('YYYY-MM-DD');
    const endpoint = date === range.start || date === range.end;
    const inRange = date >= range.start && date <= (range.end || range.start);
    const multiple = range.end && range.end !== range.start;
    return (
        <Box
            component="span"
            sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                minWidth: 0,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '18%',
                    bottom: '18%',
                    left: date === range.start ? '50%' : 0,
                    right: date === range.end ? '50%' : 0,
                    bgcolor: inRange && multiple ? '#CBE2FF' : 'transparent'
                }
            }}
        >
            <PickersDay
                {...props}
                ref={ref}
                day={day}
                disableMargin
                selected={endpoint}
                aria-selected={inRange}
                aria-label={day.format('DD/MM/YYYY')}
                onDaySelect={(selectedDay) => {
                    onSelect(selectedDay);
                    onDaySelect(selectedDay);
                }}
                sx={{
                    width: 36,
                    height: 36,
                    maxWidth: '100%',
                    position: 'relative',
                    fontSize: '0.875rem',
                    '&.Mui-selected': { bgcolor: '#1976D2', color: '#FFFFFF' },
                    '&.Mui-selected:hover, &.Mui-selected:focus': { bgcolor: '#1565C0' }
                }}
            />
        </Box>
    );
});

RangeDay.propTypes = {
    day: PropTypes.object.isRequired,
    range: PropTypes.shape({ start: PropTypes.string.isRequired, end: PropTypes.string })
        .isRequired,
    onSelect: PropTypes.func.isRequired,
    onDaySelect: PropTypes.func.isRequired
};

const AgendaCalendar = ({ initialRange, onApply, zone }) => {
    const { t, i18n } = useTranslation();
    const [range, setRange] = useState(initialRange);
    const [awaitingEnd, setAwaitingEnd] = useState(false);
    const [error, setError] = useState(false);
    const [calendarVersion, setCalendarVersion] = useState(0);
    const portuguese = (i18n?.resolvedLanguage || i18n?.language || 'pt').startsWith(
        'pt'
    );
    const localeText = (portuguese ? ptPT : enUS).components.MuiLocalizationProvider
        .defaultProps.localeText;

    const selectDay = (date) => {
        const selected = date.format('YYYY-MM-DD');
        if (!awaitingEnd) {
            setRange({ start: selected, end: null });
            setAwaitingEnd(true);
            setError(false);
            return;
        }
        const start = selected < range.start ? selected : range.start;
        const end = selected < range.start ? range.start : selected;
        const invalid = dayjs(end).diff(dayjs(start), 'day') + 1 > 7;
        setError(invalid);
        // Keep the original anchor when the interval is rejected so it can be corrected.
        if (invalid) return;
        setRange({ start, end });
        setAwaitingEnd(false);
    };

    const selectToday = () => {
        const today = dayjs().tz(zone).format('YYYY-MM-DD');
        setRange({ start: today, end: today });
        setAwaitingEnd(false);
        setError(false);
        setCalendarVersion((version) => version + 1);
    };

    return (
        <Paper
            elevation={0}
            aria-label={t('agenda.calendar')}
            sx={{
                p: 2.5,
                border: '1px solid #DCE3E8',
                borderRadius: 2,
                boxShadow: 'none',
                minWidth: 0
            }}
        >
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={portuguese ? 'pt-agenda' : 'en'}
                localeText={localeText}
            >
                <DateCalendar
                    key={calendarVersion}
                    value={dayjs(range.end || range.start)}
                    views={['day']}
                    showDaysOutsideCurrentMonth
                    fixedWeekNumber={6}
                    reduceAnimations
                    slots={{ day: RangeDay }}
                    slotProps={{ day: { range, onSelect: selectDay } }}
                    sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 'none',
                        '& .MuiPickersCalendarHeader-root': { p: 0, mx: 0, mt: 0, mb: 2 },
                        '& .MuiPickersCalendarHeader-label': {
                            fontSize: '1.125rem',
                            fontWeight: 700,
                            textTransform: 'capitalize'
                        },
                        '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
                            color: '#1976D2'
                        },
                        '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))'
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                            width: 'auto',
                            mx: 0,
                            fontSize: '0.875rem'
                        },
                        '& .MuiDayCalendar-slideTransition': { minHeight: 240 },
                        '& .MuiDayCalendar-weekContainer': { my: 0.5 }
                    }}
                />
            </LocalizationProvider>
            <Box sx={{ minHeight: 40, mt: 0.5, mb: 1 }} aria-live="polite">
                {error && (
                    <Typography variant="caption" color="error" role="alert">
                        {t('agenda.rangeError')}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 1.5
                }}
            >
                <Button
                    variant="outlined"
                    onClick={selectToday}
                    sx={{ height: 40, textTransform: 'none', fontWeight: 700 }}
                >
                    {t('agenda.today')}
                </Button>
                <Button
                    variant="contained"
                    disabled={error}
                    onClick={() => {
                        onApply({ start: range.start, end: range.end || range.start });
                        setAwaitingEnd(false);
                    }}
                    sx={{ height: 40, textTransform: 'none', fontWeight: 700 }}
                >
                    {t('agenda.apply')}
                </Button>
            </Box>
        </Paper>
    );
};

AgendaCalendar.propTypes = {
    zone: PropTypes.string.isRequired,
    initialRange: PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string
    }).isRequired,
    onApply: PropTypes.func.isRequired
};

export default AgendaCalendar;
